import React, { useState, useEffect } from "react";
import Select from "react-select";
import { components } from "react-select";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";

const CostElementPopup = ({ closePopup, editData, triggerReload }) => {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [costType, setCostType] = useState([]);
  const [costOptions, setCostOptions] = useState([]);

  useEffect(() => {
    // Fetch initial data for dropdowns
    fetchCategories();
    fetchCostTypes();

    if (editData?.id) {
      const fetchData = async () => {
        try {
          const { data } = await axiosInstance.get(
            `/costsolution/costelement/get/${editData.id}`
          );

          if (data.costelement) {
            const costelement = data.costelement;
            setCode(costelement.code || "");
            setName(costelement.name || "");
            setCategory(costelement.categoryId);
      
            // Set cost types (multi select)
            const formattedCostOptions = costelement.costTypeListDTOs?.map(cat => ({
              label: cat.name,
              value: cat.id
            })) || [];
            setCostType(formattedCostOptions.map(opt => opt.value));
            setIsEditing(true);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          toast.error("Error fetching details");
        }
      };
      fetchData();
    }
  }, [editData?.id]);

  

  const fetchCategories = async () => {
    try {
      const { data } = await axiosInstance.get(
        `/costsolution/category/autosuggestion` // Adjust this endpoint to your actual category endpoint
      );

      if (data.success && Array.isArray(data.autosuggestion)) {
        const formattedOptions = data.autosuggestion.map(item => ({
          label: item.label,
          value: item.value
        }));
        setCategoryOptions(formattedOptions);
      } else {
        setCategoryOptions([]);
      }
    } catch (error) {
      console.error("Error fetching category suggestions:", error);
      setCategoryOptions([]);
    }
  };

  const fetchCostTypes = async () => {
    try {
      const { data } = await axiosInstance.get(
        `/costsolution/costtype/autosuggestion`
      );

      if (data.success && Array.isArray(data.costtype)) {
        const formattedOptions = data.costtype.map(item => ({
          label: item.name,
          value: item.id
        }));
        setCostOptions(formattedOptions);
      } else {
        setCostOptions([]);
      }
    } catch (error) {
      console.error("Error fetching cost type suggestions:", error);
      setCostOptions([]);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const postData = {
      code,
      name,
      categoryCode: category, // Changed to match your backend expectation
      costType: costType // This should be an array of IDs
    };

    try {
      const url = isEditing
        ? `/costsolution/costelement/${editData?.id}`
        : `/costsolution/costelement`;
      const method = isEditing ? "put" : "post";

      const response = await axiosInstance[method](url, postData);

      if (response.data) {
        toast.success(
          isEditing
            ? "Cost Element updated successfully!"
            : "Cost Element saved successfully!"
        );
        triggerReload();
        closePopup();
      } else {
        toast.error(
          "Error: " + (response.data.message || "Failed to process request")
        );
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong!");
    }
  };

  const handleClear = () => {
    setCode("");
    setName("");
    setCategory("");
    setCostType([]);
  };

  const CustomOption = props => {
    const { data, isSelected } = props;
    return (
      <components.Option {...props}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%"
          }}
        >
          <span>{data.label}</span>
          {isSelected && <span style={{ color: "green" }}>✔</span>}
        </div>
      </components.Option>
    );
  };

  return (
    <div className="scope-creation-popup-overlay">
      <div className="scope-creation-popup-container">
        <div className="scope-creation-popup-header">
          <h2>{isEditing ? "Edit Cost Element" : "Create Cost Element"}</h2>
          <button className="close-button" onClick={closePopup}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="scope-creation-popup-body">
            <div className="scope-creation-form-group">
              <label htmlFor="code" className="labels">
                Code
              </label>
              <input
                type="text"
                id="code"
                name="code"
                value={code}
                onChange={e => setCode(e.target.value)}
                required
                style={{ margin: "0px",fontSize:'12px' }}
              />
            </div>

            <div className="scope-creation-form-group">
              <label htmlFor="name" className="name">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                style={{ margin: "0px",fontSize:'12px'  }}
              />
            </div>

            <div className="scope-creation-form-group">
              <label htmlFor="category" className="custom">
                Category
              </label>
              <Select
                id="category"
                name="category"
                options={categoryOptions}
                value={categoryOptions.find(opt => opt.value === category)}
                onChange={selected => setCategory(selected?.value || "")}
                placeholder="Select Category..."
                isSearchable
                className="dropdown-width"
                menuPosition="fixed"
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                  control: base => ({
                    ...base,
                    width: "100%",
                    minHeight: "37px",
                    borderRadius: "6px",
                    borderColor: "#D0D6DF",
                    fontSize: "12px",
                    "&:hover": {
                      borderColor: "#454CF2"
                    }
                  }),
                  container: base => ({
                    ...base,
                    width: "100%",
                    marginBottom: "15px"
                  }),
                  menu: base => ({
                    ...base,
                    fontSize: "13px"
                  })
                }}
              />
            </div>

            <div className="scope-creation-form-group">
              <label htmlFor="costtype" className="form-lab">
                Cost Type
              </label>
              <Select
                id="costtype"
                name="costtype"
                options={costOptions}
                isMulti
                value={costOptions.filter(option =>
                  costType.includes(option.value)
                )}
                onChange={selected =>
                  setCostType(selected ? selected.map(item => item.value) : [])
                }
                placeholder="Select Cost Type..."
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{
                  Option: CustomOption,
                  ClearIndicator: () => null
                }}
                menuPosition="fixed"
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                  control: base => ({
                    ...base,
                    width: "100%",
                    minHeight: "37px",
                    borderRadius: "6px",
                    borderColor: "#D0D6DF",
                    fontSize: "13px",
                    "&:hover": {
                      borderColor: "#454CF2"
                    }
                  }),
                  container: base => ({
                    ...base,
                    width: "100%",
                    marginBottom: "15px"
                  }),
                  menu: base => ({
                    ...base,
                    fontSize: "13px"
                  })
                }}
              />
            </div>
          </div>

          <div className="scope-creation-popup-footer">
            <button type="button" className="clear-button" onClick={handleClear}>
              Clear
            </button>
            <button type="submit" className="submit-button">
              {isEditing ? "Update" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CostElementPopup;